import { useRef, useEffect, useState, useCallback } from "react";

export interface ISpringResult {
    value: number;
    start: () => void;
}

interface ISpringOptions {
    /** Коэффициент упругости */
    tension: number;
    /** Коэффициент трения */
    friction: number;
    /** Время анимации в миллисекундах. Реальное время будет равно или чуть больше заданного. */
    timeInMs: number;
    /** Начальное значение, с которого начинается анимация */
    from: number;
    /** Конечное значение, на котором заканчивается анимация */
    to: number;
    /** Максимальное значение. Нужно, если требуется не выходить за определенные рамки значений */
    max?: number;
    /** Масса пружины */
    mass?: number;
}

const ROUND_PRECISION = 100;
const FRAME_RATE = 60;

/**
 * @description
 * Хук для spring-анимаций
 */
export const useSpringAnimation = (options: ISpringOptions): ISpringResult => {
    const { tension, friction, timeInMs, from, to, max, mass = 1 } = options;

    if (mass <= 0 || tension <= 0) {
        throw new Error("mass и tension должны быть больше нуля");
    }

    const requestId = useRef<number>(0);
    const startTime = useRef<number | null>(null);
    const velocity = useRef<number>(0);
    const currentValue = useRef<number>(from);
    const [value, setValue] = useState<number>(from);

    const roundValue = useCallback((value: number) => {
        return Math.round(value * ROUND_PRECISION) / ROUND_PRECISION;
    }, []);

    const step = useCallback(
        (timestamp: number) => {
            if (startTime.current === null) {
                return;
            }

            const elapsed = timestamp - startTime.current;

            /** Если время прошло, завершаем анимацию и обнуляем промежуточные вычисления */
            if (elapsed >= timeInMs) {
                cancelAnimationFrame(requestId.current);
                startTime.current = null;
                velocity.current = 0;
                currentValue.current = to;
                setValue(to);
                return;
            }

            /**
             На каждом шаге считаем силу упругости, силу трения и ускорение пружины.
             *
             */
            const springForce = -tension * (currentValue.current - to);
            const dampingForce = -friction * velocity.current;
            const acceleration = (springForce + dampingForce) / mass;

            /** Скорость увеличивается пропорционально ускорению, но так, чтобы попасть
             * в заданную кадровую частоту
             */
            velocity.current += acceleration / FRAME_RATE;
            const roundedValue = roundValue(
                currentValue.current + velocity.current / FRAME_RATE,
            );

            /**
             * Убеждаемся, что новое значение не выходит за пределы максимального, если оно задано.
             */
            currentValue.current = max
                ? Math.min(roundedValue, max)
                : roundedValue;

            setValue(currentValue.current);
            requestId.current = requestAnimationFrame(step);
        },
        [tension, friction, mass, timeInMs, to, max, roundValue],
    );

    const start = useCallback(() => {
        velocity.current = 0;
        startTime.current = performance.now();
        currentValue.current = from;
        setValue(from);
        requestId.current = requestAnimationFrame(step);
    }, [from, step]);

    useEffect(() => {
        return () => {
            if (requestId.current) {
                cancelAnimationFrame(requestId.current);
            }
        };
    }, []);

    return { value, start };
};
